import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { useSelector } from 'react-redux';
import { lightTheme, darkTheme } from './theme';
import { SupabaseProvider } from './contexts/SupabaseContext';
import { AccessProvider } from './contexts/AccessContext';
import OnlineStatus from './components/OnlineStatus';
import InstallPWAButton from './components/InstallPWAButton';

// Pages
import Dashboard from './pages/Dashboard';
import Transactions from './pages/Transactions';
import Wallets from './pages/Wallets';
import Investments from './pages/Investments';
import Loans from './pages/Loans';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ProfileUpdate from './pages/ProfileUpdate';
import ChangePassword from './pages/ChangePassword';
import CreditCards from './pages/CreditCards';
import Statements from './pages/Statements';
import FinanceCalculator from './pages/FinanceCalculator';
import NotFound from './components/NotFound';
import AssetClassDetails from './pages/AssetClassDetails';
import ProductDetails from './pages/ProductDetails';
import CashAndLiquid from './pages/asset-classes/CashAndLiquid';
import Equity from './pages/asset-classes/Equity';
import Debt from './pages/asset-classes/Debt';
import Gold from './pages/asset-classes/Gold';
import RealEstate from './pages/asset-classes/RealEstate';
import Crypto from './pages/asset-classes/Crypto';
import Others from './pages/asset-classes/Others';
import SavingsBank from './pages/products/cash-and-liquid/SavingsBank';
import LiquidETF from './pages/products/cash-and-liquid/LiquidETF';
import MutualFund from './pages/products/cash-and-liquid/MutualFund';
import Stocks from './pages/products/equity/Stocks';
import StockPLReport from './pages/products/equity/StockPLReport';
import EquityETF from './pages/products/equity/EquityETF';
import MutualFundEquity from './pages/products/equity/MutualFundEquity';
import NPS from './pages/products/equity/NPS';
import EquityOthers from './pages/products/equity/EquityOthers';
import REIT from './pages/products/real-estate/REIT';
import RealEstateDirect from './pages/products/real-estate/RealEstateDirect';
import GoldBonds from './pages/products/gold/GoldBonds';
import GoldETF from './pages/products/gold/GoldETF';
import DigitalGold from './pages/products/gold/DigitalGold';
import GoldMutualFund from './pages/products/gold/GoldMutualFund';
import PhysicalGold from './pages/products/gold/PhysicalGold';
import GovtBonds from './pages/products/debt/GovtBonds';
import CorpBonds from './pages/products/debt/CorpBonds';
import NCD from './pages/products/debt/NCD';
import FixedDeposit from './pages/products/debt/FixedDeposit';
import DebtMutualFund from './pages/products/debt/DebtMutualFund';
import PPF from './pages/products/debt/PPF';
import EPF from './pages/products/debt/EPF';
import DebtOthers from './pages/products/debt/DebtOthers';
import NPSdbt from './pages/products/debt/NPSdbt';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RecurringDeposit from './pages/products/debt/RecurringDeposit';
import Utilities from './pages/utilities/Utilities';
import FuelEfficiency from './pages/utilities/FuelEfficiency';
import VehicleDetails from './pages/utilities/VehicleDetails';
import FastagManager from './pages/utilities/FastagManager';
import FastagDetails from './pages/utilities/FastagDetails';
import Insurance from './pages/insurance/Insurance';
import Analysis from './pages/Analysis';
import InsuranceDetails from './pages/insurance/InsuranceDetails';

// Components
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import Admin from './pages/admin/Admin';

function App() {
  // OnlineStatus is rendered at the top so it overlays all pages

  const darkMode = useSelector((state) => state.settings.darkMode);

  document.title = 'Mickles';

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      <OnlineStatus />
      <InstallPWAButton />
      <SupabaseProvider>
        <AccessProvider>
        <Router>
          <Routes>
            {/* Auth routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:accessToken" element={<ResetPassword />} />
            <Route path="/change-password" element={<ChangePassword />} />

            {/* Public legal pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />

            <Route element={<Layout />}>
              {/* Public calculator page */}
              <Route path="/finance-calculator" element={<FinanceCalculator />} />
              {/* Specific product routes must come before wildcards */}
              <Route path="/investments/cash-and-liquid/savings-bank" element={
                <PrivateRoute><SavingsBank /></PrivateRoute>
              } />
              <Route path="/investments/cash-and-liquid/liquid-etf" element={
                <PrivateRoute><LiquidETF /></PrivateRoute>
              } />
              <Route path="/investments/cash-and-liquid/mutual-fund" element={
                <PrivateRoute><MutualFund /></PrivateRoute>
              } />
              <Route path="/investments/equity/stocks" element={
                <PrivateRoute><Stocks /></PrivateRoute>
              } />
              <Route path="/investments/equity/stocks/pl-report" element={
                <PrivateRoute><StockPLReport /></PrivateRoute>
              } />
              <Route path="/investments/equity/equity-etf" element={
                <PrivateRoute><EquityETF /></PrivateRoute>
              } />
              <Route path="/investments/equity/mutual-fund-equity" element={
                <PrivateRoute><MutualFundEquity /></PrivateRoute>
              } />
              <Route path="/investments/equity/nps" element={
                <PrivateRoute><NPS /></PrivateRoute>
              } />
              <Route path="/investments/equity/others" element={
                <PrivateRoute><EquityOthers /></PrivateRoute>
              } />
              <Route path="/investments/real-estate/reit" element={
                <PrivateRoute><REIT /></PrivateRoute>
              } />
              <Route path="/investments/real-estate/real-estate-direct" element={
                <PrivateRoute><RealEstateDirect /></PrivateRoute>
              } />
              {/* Add gold product routes */}
              <Route path="/investments/gold/gold-bonds" element={
                <PrivateRoute><GoldBonds /></PrivateRoute>
              } />
              <Route path="/investments/gold/gold-etf" element={
                <PrivateRoute><GoldETF /></PrivateRoute>
              } />
              <Route path="/investments/gold/digital-gold" element={
                <PrivateRoute><DigitalGold /></PrivateRoute>
              } />
              <Route path="/investments/gold/gold-mutual-fund" element={
                <PrivateRoute><GoldMutualFund /></PrivateRoute>
              } />
              <Route path="/investments/gold/physical-gold" element={
                <PrivateRoute><PhysicalGold /></PrivateRoute>
              } />
              {/* Add debt product routes */}
              <Route path="/investments/debt/govt-bonds" element={
                <PrivateRoute><GovtBonds /></PrivateRoute>
              } />
              <Route path="/investments/debt/corp-bonds" element={
                <PrivateRoute><CorpBonds /></PrivateRoute>
              } />
              <Route path="/investments/debt/ncd" element={
                <PrivateRoute><NCD /></PrivateRoute>
              } />
              <Route path="/investments/debt/fd" element={
                <PrivateRoute><FixedDeposit /></PrivateRoute>
              } />
              <Route path="/investments/debt/mutual-fund" element={
                <PrivateRoute><DebtMutualFund /></PrivateRoute>
              } />
              <Route path="/investments/debt/ppf" element={
                <PrivateRoute><PPF /></PrivateRoute>
              } />
              <Route path="/investments/debt/epf" element={
                <PrivateRoute><EPF /></PrivateRoute>
              } />
              <Route path="/investments/debt/nps" element={
                <PrivateRoute><NPSdbt /></PrivateRoute>
              } />
              <Route path="/investments/debt/others" element={
                <PrivateRoute><DebtOthers /></PrivateRoute>
              } />
              <Route path="/investments/debt/recurring-deposit" element={
                <PrivateRoute><RecurringDeposit /></PrivateRoute>
              } />

              {/* Asset class routes */}
              <Route path="/investments/:assetClass" element={
                <PrivateRoute><AssetClassDetails /></PrivateRoute>
              } />
              <Route path="/investments/:assetClass/:product" element={<ProductDetails />} />

              {/* Generic routes */}
              <Route path="/investments" element={
                <PrivateRoute><Investments /></PrivateRoute>
              } />
              <Route path="/investments/cash-and-liquid/*" element={
                <PrivateRoute>
                  <CashAndLiquid />
                </PrivateRoute>
              } />
              <Route path="/investments/equity/*" element={
                <PrivateRoute>
                  <Equity />
                </PrivateRoute>
              } />
              <Route path="/investments/debt/*" element={
                <PrivateRoute>
                  <Debt />
                </PrivateRoute>
              } />
              <Route path="/investments/gold/*" element={
                <PrivateRoute>
                  <Gold />
                </PrivateRoute>
              } />
              <Route path="/investments/real-estate/*" element={
                <PrivateRoute>
                  <RealEstate />
                </PrivateRoute>
              } />
              <Route path="/investments/crypto/*" element={
                <PrivateRoute>
                  <Crypto />
                </PrivateRoute>
              } />
              <Route path="/investments/others/*" element={
                <PrivateRoute>
                  <Others />
                </PrivateRoute>
              } />

              {/* Other main routes */}
              <Route path="/" element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              } />
              <Route path="/transactions" element={
                <PrivateRoute>
                  <Transactions />
                </PrivateRoute>
              } />
              <Route path="/wallets" element={
                <PrivateRoute>
                  <Wallets />
                </PrivateRoute>
              } />
              <Route path="/loans" element={
                <PrivateRoute>
                  <Loans />
                </PrivateRoute>
              } />
              <Route path="/admin" element={
                <AdminRoute>
                  <Admin />
                </AdminRoute>
              } />
              <Route path="/analysis" element={
                <PrivateRoute>
                  <Analysis />
                </PrivateRoute>
              } />
              <Route path="/insurance" element={
                <PrivateRoute>
                  <Insurance />
                </PrivateRoute>
              } />
              <Route path="/insurance/:policyId" element={
                <PrivateRoute>
                  <InsuranceDetails />
                </PrivateRoute>
              } />
              <Route path="/credit-cards" element={
                <PrivateRoute>
                  <CreditCards />
                </PrivateRoute>
              } />
              <Route path="/statements" element={
                <PrivateRoute>
                  <Statements />
                </PrivateRoute>
              } />
              <Route path="/profile-update" element={
                <PrivateRoute>
                  <ProfileUpdate />
                </PrivateRoute>
              } />
              {/* Utilities */}
              <Route path="/utilities" element={
                <PrivateRoute>
                  <Utilities />
                </PrivateRoute>
              } />
              <Route path="/utilities/fuel-efficiency" element={
                <PrivateRoute>
                  <FuelEfficiency />
                </PrivateRoute>
              } />
              <Route path="/utilities/fuel-efficiency/:vehicleId" element={
                <PrivateRoute>
                  <VehicleDetails />
                </PrivateRoute>
              } />
              <Route path="/utilities/fastag" element={
                <PrivateRoute>
                  <FastagManager />
                </PrivateRoute>
              } />
              <Route path="/utilities/fastag/:tagId" element={
                <PrivateRoute>
                  <FastagDetails />
                </PrivateRoute>
              } />
              {/* Route for CashAndLiquid */}
              <Route
                path="/investments/cash-and-liquid"
                element={
                  <PrivateRoute>
                    <CashAndLiquid />
                  </PrivateRoute>
                }
              />
              {/* Route for ProductDetails */}
              <Route
                path="/investments/cash-and-liquid/:product"
                element={
                  <PrivateRoute>
                    <ProductDetails />
                  </PrivateRoute>
                }
              />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
        </AccessProvider>
      </SupabaseProvider>
    </ThemeProvider>
  );
}

export default App;